"use client";
/**
 * Floating feedback button that triggers PostHog surveys.
 * Positioned fixed in bottom-right corner, visible on authenticated pages that do not use the sidebar (see ClientProvider).
 * Never on Lev Mobile: the bottom-right corner there is the bottom nav's Brain Dump tab.
 *
 * Setup:
 * 1. Create an "Open Feedback" survey in PostHog
 * 2. Copy the survey ID from PostHog URL (e.g., /surveys/01234567-abcd-...)
 * 3. Set NEXT_PUBLIC_POSTHOG_SURVEY_ID in .env
 */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import { MessageSquarePlus } from "lucide-react";

import { openPostHogFeedback } from "@/lib/feedback";
import { trackFeedbackButtonClicked } from "@/lib/analytics/client";
import { isMobileRoute } from "@/lib/routes";

const FEEDBACK_SURVEY_ID = process.env.NEXT_PUBLIC_POSTHOG_SURVEY_ID;

export function FeedbackButton() {
  const { isSignedIn } = useAuth();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Don't render until mounted to avoid hydration mismatch (server doesn't know auth state)
  // Also don't render if survey ID not configured or user not signed in
  if (!mounted || !FEEDBACK_SURVEY_ID || !isSignedIn || isMobileRoute(pathname)) {
    return null;
  }

  return (
    <button
      onClick={() => {
        trackFeedbackButtonClicked({ location: "floating" });
        openPostHogFeedback();
      }}
      className="fixed bottom-6 right-6 z-feedback-button flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-all hover:bg-primary/90"
      aria-label="Send feedback"
      title="Send feedback"
    >
      <MessageSquarePlus className="h-5 w-5" />
    </button>
  );
}
